// Copyright (C) 2022 Intel Corporation
//
// SPDX-License-Identifier: MIT
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { activateObject, updateAnnotationsAsync } from 'actions/annotation-actions';
import { getSampleAsync } from './sample-actions';
import { semiCircleImpl } from '../../../../../external-libraries/semicircle/SemiCircle';
import {
    map as L_map, tileLayer as L_tileLayer, control as L_control, polygon as L_polygon, latLng as L_Latlng,
}
    from '../../../../../external-libraries/leaflet/leaflet-src.esm';
import '../../../../../external-libraries/leaflet/leaflet.css';

/* eslint no-underscore-dangle: 1 */
/* eslint @typescript-eslint/no-unused-vars: 1 */

let reduxState: any;

interface StateToProps {
    activatedStateID: number | null;
    activatedAttributeID: number | null;
    states: any[];
}

interface DispatchToProps {
    activateObjectDispatch(activatedStateID: number | null, activatedAttributeID: number | null): void;
    updateAnnotations(updatedAnnotationsArray: any[]): void;
    getSample(imageName: string): void;
}

function getActiveAnnotationObjectIndex(activeAnnotationId: number): number {
    return reduxState.annotation.annotations.states.findIndex((state: any) => state.clientID === activeAnnotationId);
}

function activateHighlightedObject(row : any, activateObjectDispatch: Function): void {
    let activatedStateID : any;
    for (const annotationItem of reduxState.annotation.annotations.states) {
        const attributeIndexItems = Object
            .keys(annotationItem.attributes)
            .map(Number);
        // The ais attribute can be in any location, iterate over all of them
        for (const indexItem of attributeIndexItems) {
            const aisDataItem = annotationItem.attributes[indexItem];
            if (aisDataItem === row.ais_mmsi.toString()) {
                activatedStateID = annotationItem.clientID;
                activateObjectDispatch(activatedStateID, null);
                return;
            }
        }
    }
}

function getAttributeId(attrName: string, attributesConfigState: any, activeAnnotationObject: any): number {
    for (const label of attributesConfigState) {
        const labelName = label.name.toLowerCase();
        const activeAnnotObjLabelName = activeAnnotationObject.label.name.toLowerCase();
        if (labelName === activeAnnotObjLabelName) {
            const { attributes } = label;
            for (const attribute of attributes) {
                if (attribute.name === attrName) {
                    return parseInt(attribute.id, 10);
                }
            }
        }
    }
    return -1;
}

async function attachAisToAttribute(aisId: string, updateAnnotations: Function,
    activeAnnotationObject: any): Promise<void> {
    const attr: Record<number, string> = {};
    const attributesConfigState = reduxState.annotation.job.labels;
    const attrId: number = getAttributeId('ais_data', attributesConfigState, activeAnnotationObject);

    attr[attrId] = aisId;
    activeAnnotationObject.attributes = attr;
    updateAnnotations([activeAnnotationObject]);
}

function findNewPoint(x : number, y : number, angle : number, distance : number): number[] {
    const newX = Math.cos((angle * Math.PI) / 180) * distance + x;
    const newY = Math.sin((angle * Math.PI) / 180) * distance + y;
    return [newX, newY];
}

function triangle(x : number, y : number, angle : number, length : number, spread = 15): number[][] {
    const A = findNewPoint(x, y, (angle - spread) % 360, length);
    const B = findNewPoint(x, y, (angle + spread) % 360, length);
    const C = [x, y];
    return [C, A, B];
}

function createSemiCircleFov(mapLocal: any, shipsCords: Array<number>, fovDir: number, setMapObjects: Function): void {
    const SemiCircle = semiCircleImpl();
    const lSemiCircle = SemiCircle([shipsCords[0], shipsCords[1]], {
        radius: 7000,
        color: 'blue',
        fillColor: 'red',
        fillOpacity: 0.2,
        weight: 1,
    }).setDirection(fovDir, 225);
    lSemiCircle.addTo(mapLocal);
    setMapObjects((oldArray: any[]) => [...oldArray, lSemiCircle]);
}

function toRadians(degree : number): number {
    return (degree * Math.PI) / 180;
}

function moveShipForward(lat : number, lon : number, head : number): number[] {
    const earthRadius = 6378.1;
    const distance = 0.15;

    const toDegrees = function (rad : number): number {
        return rad * (180 / Math.PI);
    };

    const lat1 = toRadians(lat);
    const lon1 = toRadians(lon);

    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(distance / earthRadius) +
                Math.cos(lat1) * Math.sin(distance / earthRadius) * Math.cos(toRadians(head)));
    const lon2 = lon1 + Math.atan2(Math.sin(toRadians(head)) * Math.sin(distance / earthRadius) * Math.cos(lat1),
        Math.cos(distance / earthRadius) - Math.sin(lat1) * Math.sin(lat2));

    return [toDegrees(lat2), toDegrees(lon2)];
}

function getDistanceHaversine(mainShipCords : Array < number >, destinationCords : Array < number >): string {
    const lon1 = mainShipCords[0];
    const lat1 = mainShipCords[1];
    const lon2 = destinationCords[0];
    const lat2 = destinationCords[1];

    const R = 6371e3; // metres
    const φ1 = toRadians(lat1); // φ, λ in radians
    const φ2 = toRadians(lat2);
    const Δφ = toRadians((lat2 - lat1));
    const Δλ = toRadians((lon2 - lon1));

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distanceMeter: number = R * c;
    if (distanceMeter.toFixed(0).toString().length > 3) {
        return `${(distanceMeter / 1000).toFixed(1)} km`; // in km
    }
    return `${distanceMeter.toFixed(1)} m`; // in metres
}

function updateAisColorAndAttribute(e: any, updateAnnotations: Function): void {
    const targetElement = e.target;

    const { annotations } = reduxState.annotation;
    const activeAnnotationObjectIndex = getActiveAnnotationObjectIndex(annotations.activatedStateID);
    const activeAnnotationObject = annotations.states[activeAnnotationObjectIndex];
    if (!activeAnnotationObject) {
        alert('Please highlight the desired annotation first!');
        return;
    }

    if (targetElement.options.fillColor === 'green') {
        // if clicked ship was green (active), make it black (inactive) and setup "None" as attribute
        attachAisToAttribute('None', updateAnnotations, activeAnnotationObject);
    } else if (targetElement.options.fillColor === 'black') {
        // if clicked ship was black (inactive), make it green (active) and setup "aisId" as attribute
        attachAisToAttribute(e.target.aisId.toString(), updateAnnotations, activeAnnotationObject);
    }
}

function addShipToMap(mapLocal : any, aisData : any, activateObjectDispatch: Function, updateAnnotations: Function,
    isMainShip : boolean, mainShipCords : Array < number >, setMapObjects: Function): void {
    const aisLon = isMainShip ?
        aisData.gps_location.lon :
        aisData.ais_location.lon;
    const aisLat = isMainShip ?
        aisData.gps_location.lat :
        aisData.ais_location.lat;
    const head = aisData.ais_heading;

    const [latFore,
        lonFore] = moveShipForward(aisLat, aisLon, head);

    const direction = (+head + 180) % 360;
    const polygon = triangle(+latFore, +lonFore, direction, 0.0023);
    const distance = mainShipCords.length === 0 ?
        0 :
        getDistanceHaversine(mainShipCords, [aisLon, aisLat]);

    const shipPoly = L_polygon(polygon, {
        color: 'white',
        fillColor: isMainShip ?
            'red' :
            'black',
        fillOpacity: 1,
        weight: 0.4,
        className: isMainShip ?
            'mainship-polygon' :
            'othership-polygon',
    });
    shipPoly.aisId = aisData.ais_mmsi;

    shipPoly
        .addTo(mapLocal)
        .on('mouseover', () => {
            activateHighlightedObject(aisData, activateObjectDispatch);
        })
        .on('click', (e : any) => {
            updateAisColorAndAttribute(e, updateAnnotations);
        })
        .bindTooltip([
            '<b>Name:</b>', aisData.ais_name, '<br><b>MMSI_NUMBER:</b>', aisData.ais_mmsi,
            '<br><b>Type:</b>',
            aisData.ais_shiptype_str,
            '<br><b>Destination:</b>',
            aisData.ais_destination,
            '<br><b>Length:</b>',
            aisData.ais_dimensions_length, ' m',
            '<br><b>Width:</b>',
            aisData.ais_dimensions_width, ' m',
            '<br><b>Distance:</b>',
            distance,
        ].join(' '));

    setMapObjects((oldArray: any[]) => [...oldArray, shipPoly]);
    if (!aisData.ais_heading) {
        // The main ship is sometimes lacking ais info
        // GNSS heading can have a ofsset e.g. 90 degree, so it is unreliable
        // Don't draw foV indicator in that case
        return;
    }

    if (isMainShip) {
        const fovDir = +head % 360;
        // if image is from groke pro then fov is semicircle
        const rgbHeight = aisData.rgb_1_height;
        const rgbWidth = aisData.rgb_1_width;
        const isPro = (rgbWidth / 4) > rgbHeight;
        if (isPro) {
            createSemiCircleFov(mapLocal, [aisLat, aisLon], fovDir, setMapObjects);
        } else {
            const LSemiCircle = semiCircleImpl();
            const lSemiCircle = LSemiCircle([aisLat, aisLon], {
                radius: 10000,
                color: 'blue',
                fillColor: 'red',
                fillOpacity: 0.05,
                weight: 1,
            }).setDirection(fovDir, 90);
            lSemiCircle.addTo(mapLocal);
            setMapObjects((oldArray: any[]) => [...oldArray, lSemiCircle]);
        }
    }
}

function addDataToMap(mapLocal : any, aisData : any, activateObjectDispatch: Function,
    updateAnnotations: Function, setMapObjects: Function): void {
    // add the main ship first
    const mainShipGpsCoords = aisData.gps_location;
    if (mainShipGpsCoords) {
        const mainShipCords = [mainShipGpsCoords.lon, mainShipGpsCoords.lat,
        ];
        // center the map view to the main ship every time
        mapLocal.panTo(L_Latlng(mainShipGpsCoords.lat, mainShipGpsCoords.lon));
        addShipToMap(mapLocal, aisData, activateObjectDispatch, updateAnnotations, true, [], setMapObjects);
        const aisPoints = aisData.ais_window;
        if (aisPoints) {
            aisPoints.forEach((row : any, _i: any) => { // eslint-disable-line no-unused-vars
                addShipToMap(mapLocal, row, activateObjectDispatch, updateAnnotations, false,
                    mainShipCords, setMapObjects);
            });
        }
    }
}

// clear all the objects from map before adding new
function removeDataFromMap(mapObjects: any[], mapLocal: any): void {
    mapObjects.forEach((item) => {
        mapLocal.removeLayer(item);
    });
}

function resetColorsForInactiveObjects(mapObjects: any[]): void {
    mapObjects.forEach((mapObject) => {
        if (mapObject.options.fillColor === 'green') {
            mapObject.setStyle({ fillColor: 'black' });
        }
    });
}

function highlightShipInMap(activatedObjectId: string, mapObjects: any[]): void {
    // if no annotation is selected, set all objects to black color
    if (!activatedObjectId) {
        resetColorsForInactiveObjects(mapObjects);
        return;
    }
    const activatedAnnotationObject = reduxState.annotation.annotations.states.filter(
        (annotation: any) => (annotation.clientID === activatedObjectId),
    )[0];
    const aisId: number = getAttributeId('ais_data', reduxState.annotation.job.labels, activatedAnnotationObject);
    const shouldBeActivatedShipObject = mapObjects.filter(
        (mapObject) => (mapObject.aisId && mapObject.aisId.toString() === activatedAnnotationObject.attributes[aisId]),
    )[0];

    resetColorsForInactiveObjects(mapObjects);
    if (shouldBeActivatedShipObject) {
        shouldBeActivatedShipObject.setStyle({ fillColor: 'green' });
    }
}

function AisMap(props: StateToProps & DispatchToProps) : JSX.Element {
    const aisData = reduxState.sample.data._source;
    const imageName = reduxState.annotation.player.frame.filename;
    const activatedObjectId = reduxState.annotation.annotations.activatedStateID;
    const [mapLocal, setMapLocal] = useState(null);
    // mapObjects to keep track of all the objects added to the map.
    const [mapObjects, setMapObjects] = useState([]);

    const {
        getSample,
        activateObjectDispatch,
        updateAnnotations,
    } = props;

    useEffect(() => {
        getSample(imageName);
    }, [imageName]);

    useEffect(() => {
        if (aisData) {
            removeDataFromMap(mapObjects, mapLocal);
            setMapObjects([]);
        }
    }, [aisData]);

    useEffect(() => {
        if (aisData && mapObjects.length === 0) {
            if (mapLocal == null) {
                const newMapLocal = L_map('map', { zoomSnap: 0.25 }).setView([
                    35.3789, 139.7252,
                ], 13);
                L_tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(newMapLocal);
                L_control.scale().addTo(newMapLocal);
                setMapLocal(newMapLocal);
                addDataToMap(newMapLocal, aisData, activateObjectDispatch, updateAnnotations, setMapObjects);
            } else {
                addDataToMap(mapLocal, aisData, activateObjectDispatch, updateAnnotations, setMapObjects);
            }
        }
    }, [mapObjects]);

    useEffect(() => {
        highlightShipInMap(activatedObjectId, mapObjects);
    }, [activatedObjectId]);

    useEffect(() => {
        highlightShipInMap(activatedObjectId, mapObjects);
    }, [reduxState.annotation.annotations.states]);

    return (
        <div
            id='map'
            style={{
                height: '50%',
                width: '500px',
                clear: 'both',
            }}
        />
    );
}

function mapStateToProps(state : any): any {
    reduxState = state;
    return reduxState;
}

function mapDispatchToProps(dispatch: any): any {
    return {
        getSample(imageName: string) {
            dispatch(getSampleAsync(imageName));
        },

        activateObjectDispatch(activatedStateID: number, activatedAttributeID: number): void {
            dispatch(activateObject(activatedStateID, activatedAttributeID));
        },
        updateAnnotations(updatedAnnotationsArray: any[]): void {
            dispatch(updateAnnotationsAsync(updatedAnnotationsArray));
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(AisMap));
